import { UploadPhotoTemplate } from '@templates';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ErrorMessages } from '@types';
import * as Sentry from '@sentry/react';
import { FileObject, useUploadImage, useDeleteImage } from '@hooks';
import {
  convertFilePartsToFileObjects,
  handleFileChange,
  handleFileDelete,
  handleUploadFile,
} from '@utils';
import { RoutesList } from '@abvehicles/routes/Routes';
import { useAbVehiclesStore } from '@abvehicles/store/store';
import { UploadPhotoTextContent } from './UploadPhotoTextContent';

export const UploadPhotoPage = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState<FileObject[]>([]);
  const {
    uploadedImages,
    uploadedImagesFileName,
    uploadedImagesFileType,
    uploadedImagesBlob,
    updateUploadedImages,
    updateIsPhotoLocationStored,
    fileWithLoadedCoordinates,
    updateFileWithLoadedCoordinates,
    updateServiceLocationDetails,
    updateUploadedImagesBlobs,
  } = useAbVehiclesStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  // use mutateAsync due to async operations after successful upload
  const { mutateAsync: mutateImageUpload, isPending } = useUploadImage();
  const { mutate: mutateDelete } = useDeleteImage();
  const [processingPhoto, setProcessingPhoto] = useState(false);

  useEffect(() => {
    // retrieve previously uploaded images from localStorage and display them on page load
    convertFilePartsToFileObjects({
      uploadedImages,
      uploadedImagesFileName,
      uploadedImagesFileType,
      uploadedImagesBlob,
    }).then((images) => {
      setUploadedFiles(images);
    });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onFileChange = async (files: File[]) => {
    await handleFileChange({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages,
      updateIsPhotoLocationStored,
      updateFileWithLoadedCoordinates,
      updateServiceLocationDetails,
      errorPath: 'upload-photo',
    });
  };

  const handleDelete = (files: File[]) => {
    handleFileDelete({
      files,
      setProcessingPhoto,
      uploadedFiles,
      setUploadedFiles,
      setErrorMessages,
      updateUploadedImages,
      updateIsPhotoLocationStored,
      fileWithLoadedCoordinates,
      updateFileWithLoadedCoordinates,
      updateServiceLocationDetails,
      mutateDelete,
      errorPath: 'upload-photo',
    });
  };

  const handleUploadError = (error: string) => {
    if (error) {
      Sentry.captureException(error);
      setErrorMessages([{ path: 'upload-photo', message: error }]);
    }
  };

  const buttonNext = {
    label: uploadedImages.length === 0 ? 'I do not have a photo' : 'Next',
    loading: isPending,
    disabled: processingPhoto,
    onClick: async () => {
      await handleUploadFile({
        uploadedFiles,
        uploadedImagesBlob,
        navigate,
        navigatePath: RoutesList.ISSUE_TYPE,
        mutateImageUpload,
        errorPath: 'upload-photo',
        setErrorMessages,
        updateUploadedImagesBlobs,
      });
    },
  };

  const buttonPrevious = {
    disabled: processingPhoto || isPending,
    onClick: () => {
      navigate(RoutesList.LEAKING);
    },
  };
  return (
    <UploadPhotoTemplate
      formTitle="Upload Photos"
      description={<UploadPhotoTextContent />}
      handleFileChange={onFileChange}
      handleUploadError={handleUploadError}
      handleDelete={handleDelete}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      maxNumberOfUpload={5}
      errorPath={'upload-photo'}
      errorMessages={errorMessages}
      initialFiles={uploadedFiles.map((file) => file.file)}
    />
  );
};
